/**
 * +prof: live gathering state for the HUD (hud/professions/*), written by world/worldApp.ts, the
 * same polling pattern as fieldView (plain fields, `*Version` counters). DOM-free.
 */
import type { GatherProfession } from '@keystone/sim';
import type { GatherMode } from '@keystone/bots';

export interface ProfHover {
  /** Screen pixel of the cursor. */
  x: number;
  y: number;
  nodeId: string;
  /** Ms until it is back (0 = ready). */
  backInMs: number;
  /** Distance from the hero (yd). */
  dist: number;
}

export interface ProfChannel {
  nodeId: string;
  name: string;
  profession: GatherProfession;
  /** performance.now() at the start, and the length. */
  at: number;
  durationMs: number;
}

/** A skill-up / reward line to flash (the HUD drains it). */
export interface ProfToast {
  kind: 'skill' | 'tip' | 'rare';
  title: string;
  text: string;
  at: number;
}

/** A tracked node on screen (or pinned to the edge), recomputed per frame by `markers`. */
export interface ProfMarker {
  x: number;
  y: number;
  /** Off screen: pinned to the edge and pointing `angle` (radians, screen space). */
  edge: boolean;
  angle: number;
  color: string;
  name: string;
  dist: number;
}

export interface ProfActions {
  togglePanel(): void;
  setAutoMode(mode: GatherMode): void;
  setTracking(prof: GatherProfession | null): void;
}

export interface ProfView {
  active: boolean;
  skills: Record<GatherProfession, number>;
  gathers: number;
  byKind: Record<string, number>;
  skillsVersion: number;
  panelOpen: boolean;
  panelVersion: number;
  autoMode: GatherMode;
  tracking: GatherProfession | null;
  settingsVersion: number;
  hover: ProfHover | null;
  hoverVersion: number;
  channel: ProfChannel | null;
  channelVersion: number;
  toasts: ProfToast[];
  /** Tracked nodes to mark this frame (set by the world while a session runs). */
  markers: (() => ProfMarker[]) | null;
  actions: ProfActions | null;
}

export const profView: ProfView = {
  active: false,
  skills: { foraging: 1, mining: 1 },
  gathers: 0,
  byKind: {},
  skillsVersion: 0,
  panelOpen: false,
  panelVersion: 0,
  autoMode: 'hunt',
  tracking: null,
  settingsVersion: 0,
  hover: null,
  hoverVersion: 0,
  channel: null,
  channelVersion: 0,
  toasts: [],
  markers: null,
  actions: null,
};

export function resetProfView(): void {
  const v = profView;
  v.active = false;
  v.hover = null;
  v.channel = null;
  v.panelOpen = false;
  v.markers = null;
  v.actions = null;
  v.hoverVersion++;
  v.channelVersion++;
  v.panelVersion++;
}

// ---------------------------------------------------------------------------
// Per-viewer settings (localStorage, guarded): the Auto setting, tracking, the first-node tip.
// ---------------------------------------------------------------------------

const KEY = 'keystone.prof.v1';
interface Stored {
  autoMode?: GatherMode;
  tracking?: GatherProfession | null;
  tipSeen?: boolean;
}
function read(): Stored {
  try {
    const raw = typeof localStorage === 'undefined' ? null : localStorage.getItem(KEY);
    const o = raw ? (JSON.parse(raw) as Stored) : {};
    return o && typeof o === 'object' ? o : {};
  } catch {
    return {};
  }
}
function write(patch: Stored): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(KEY, JSON.stringify({ ...read(), ...patch }));
  } catch {
    /* private mode: the setting lasts this session */
  }
}

export const AUTO_MODES: readonly GatherMode[] = ['hunt', 'gather', 'both'];
export const AUTO_MODE_LABEL: Readonly<Record<GatherMode, string>> = { hunt: 'Hunt', gather: 'Gather', both: 'Both' };

export function loadProfSettings(): { autoMode: GatherMode; tracking: GatherProfession | null; tipSeen: boolean } {
  const s = read();
  return {
    autoMode: s.autoMode && AUTO_MODES.includes(s.autoMode) ? s.autoMode : 'hunt',
    tracking: s.tracking === 'foraging' || s.tracking === 'mining' ? s.tracking : null,
    tipSeen: !!s.tipSeen,
  };
}
export const saveProfSettings = (patch: Stored): void => write(patch);
